import useClientPagination from 'hooks/use-client-pagination'
import UrlPagination from 'components/molecules/url-pagination'
import LegacySettingsResourcesApiV2 from 'api/v2/admins/legacy-settings-resources-api-v2'
import ArchiveFilter from 'domains/setting/components/archive-filter'
import { errorMessage } from 'domains/dashboard-migration/api'
import TitlePage from 'components/atoms/title-page'
import { ALERT_TYPES } from 'constants/alert-constants'
import { LEGACY_SETTINGS_RESOURCES } from 'domains/setting/constants/legacy-settings-resources'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table
} from 'reactstrap'

const propTypes = {
  resourceKey: PropTypes.string.isRequired,
  pageUtils  : PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const defaultProps = {
  pageUtils: {
    setAlertMsg    : () => {},
    setApiErrorMsg : () => {}
  }
}

const initialValues = (config, record = {}) => (
  config.fields.reduce((memo, field) => {
    if (field.type === 'checkbox') memo[field.key] = record[field.key] === undefined ? true : Boolean(record[field.key])
    else memo[field.key] = record[field.key] ?? ''
    return memo
  }, {})
)

const formatCell = (column, value) => {
  if (column.type === 'boolean') {
    return value
      ? <Badge color='success'>{ 'Yes' }</Badge>
      : <Badge color='secondary'>{ 'No' }</Badge>
  }

  if (column.type === 'datetime') return value ? new Date(value).toLocaleString() : '-'
  return value === null || value === undefined || value === '' ? '-' : String(value)
}

const LegacySettingsResourcePage = ({ resourceKey, pageUtils }) => {
  const config = LEGACY_SETTINGS_RESOURCES[resourceKey]
  const [ records, setRecords ] = useState([])
  const [ selectedRecord, setSelectedRecord ] = useState(null)
  const [ values, setValues ] = useState(() => initialValues(config))
  const [ isModalOpen, setIsModalOpen ] = useState(false)
  const [ isLoading, setIsLoading ] = useState(false)
  const [ isSaving, setIsSaving ] = useState(false)
  const [ query, setQuery ] = useState('')
  const [ archived, setArchived ] = useState(false)
  const [ error, setError ] = useState('')
  const [ isArchiving, setIsArchiving ] = useState(false)
  const archiveOperation = useRef(false)
  const fetchSequence = useRef(0)
  const showError = useCallback(async (err) => setError(await errorMessage(err)), [])

  const resourceResponseKey = resourceKey
  const singularResponseKey = resourceKey === 'currencies' ? 'currency' : resourceKey.replace(/s$/, '')

  const fetchRecords = useCallback(() => {
    const sequence = ++fetchSequence.current
    setError('')
    setIsLoading(true)
    LegacySettingsResourcesApiV2.get(resourceKey, archived)
      .then((response) => { if (sequence === fetchSequence.current) setRecords(response.data[resourceResponseKey] || []) })
      .catch(showError)
      .finally(() => { if (sequence === fetchSequence.current) setIsLoading(false) })
  }, [ resourceKey, resourceResponseKey, archived, showError ])

  useEffect(() => {
    fetchRecords()
  }, [ fetchRecords ])

  const filteredRecords = useMemo(() => {
    const keyword = query.trim().toLowerCase()
    if (!keyword) return records

    return records.filter((record) => (
      config.columns.some((column) => String(record[column.key] ?? '').toLowerCase().includes(keyword))
    ))
  }, [ config.columns, query, records ])

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedRecord(null)
    setValues(initialValues(config))
  }

  const openCreate = () => {
    setSelectedRecord(null)
    setValues(initialValues(config))
    setIsModalOpen(true)
  }

  const openEdit = (record) => {
    const openWithRecord = (nextRecord) => {
      setSelectedRecord(nextRecord)
      setValues(initialValues(config, nextRecord))
      setIsModalOpen(true)
    }

    if (!config.showProducts) {
      openWithRecord(record)
      return
    }

    LegacySettingsResourcesApiV2.show(resourceKey, record.id)
      .then((response) => openWithRecord(response.data[singularResponseKey] || record))
      .catch(showError)
  }

  const handleChange = (field, event) => {
    const value = field.type === 'checkbox'
      ? event.target.checked
      : field.type === 'file'
        ? event.target.files?.[0] || null
        : event.target.value

    setValues((current) => ({ ...current, [field.key]: value }))
  }

  const buildPayload = () => {
    if (!config.useFormData) return values

    const formData = new FormData()
    Object.keys(values).forEach((key) => {
      const value = values[key]
      if (value !== null && value !== undefined && value !== '') formData.append(key, value)
    })
    return formData
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    setIsSaving(true)

    const request = selectedRecord
      ? LegacySettingsResourcesApiV2.update(resourceKey, selectedRecord.id, buildPayload(), config.useFormData)
      : LegacySettingsResourcesApiV2.create(resourceKey, buildPayload(), config.useFormData)

    request
      .then(() => {
        pageUtils.setAlertMsg?.(
          `${config.singular} has been ${selectedRecord ? 'updated' : 'created'}`,
          ALERT_TYPES.SUCCESS
        )
        closeModal()
        fetchRecords()
      })
      .catch(showError)
      .finally(() => setIsSaving(false))
  }

  const handleDelete = (record) => {
    if (!config.destroy) return
    if (!window.confirm(`Archive ${config.singular} ${record.name || record.title || `#${record.id}`}? Data dan relasi tetap disimpan.`)) return

    if (archiveOperation.current) return
    archiveOperation.current = true
    setIsArchiving(true)
    LegacySettingsResourcesApiV2.destroy(resourceKey, record.id)
      .then(() => {
        pageUtils.setAlertMsg?.(`${config.singular} has been archived`, ALERT_TYPES.SUCCESS)
        fetchRecords()
      })
      .catch(showError)
      .finally(() => { archiveOperation.current = false; setIsArchiving(false) })
  }

  const handleRestore = (record) => {
    if (archiveOperation.current) return
    if (!window.confirm(`Restore ${config.singular} ${record.name || record.title || `#${record.id}`}?`)) return
    archiveOperation.current = true
    setIsArchiving(true)
    LegacySettingsResourcesApiV2.restore(resourceKey, record.id)
      .then(() => { pageUtils.setAlertMsg?.(`${config.singular} has been restored`, ALERT_TYPES.SUCCESS); fetchRecords() })
      .catch(showError)
      .finally(() => { archiveOperation.current = false; setIsArchiving(false) })
  }

  const renderField = (field) => {
    if (field.type === 'checkbox') {
      return (
        <FormGroup check key={ field.key } className='mb-3'>
          <Label check>
            <Input
              type='checkbox'
              checked={ Boolean(values[field.key]) }
              onChange={ (event) => handleChange(field, event) }
            />
            { ` ${field.label}` }
          </Label>
        </FormGroup>
      )
    }

    return (
      <FormGroup key={ field.key }>
        <Label>{ field.label }</Label>
        <Input
          type={ field.type === 'textarea' ? 'textarea' : field.type }
          step={ field.step }
          accept={ field.accept }
          value={ field.type === 'file' ? undefined : values[field.key] }
          onChange={ (event) => handleChange(field, event) }
        />
      </FormGroup>
    )
  }

  const listPagination = useClientPagination(filteredRecords, { loading: isLoading })

  return (
    <>
      <TitlePage mainTitle='Setting' subTitle={ config.title } />
      { error && !isModalOpen && <div role='alert' className='alert alert-danger'>{ error }</div> }
      <Card>
        <CardBody>
          <Row className='mb-3 align-items-center'>
            <Col md={ 5 }>
              <Input
                value={ query }
                onChange={ (event) => { listPagination.setPage(1); setQuery(event.target.value) } }
                placeholder={ `Search ${config.title.toLowerCase()}` }
              />
            </Col>
            <Col md={ 7 } className='text-md-right mt-2 mt-md-0'>
              {
                config.create && !archived && (
                  <Button color='primary' onClick={ openCreate }>
                    { `Create ${config.singular}` }
                  </Button>
                )
              }
            </Col>
          </Row>

          <ArchiveFilter value={ archived } change={ (value) => { listPagination.setPage(1); setArchived(value) } } disabled={ isArchiving } />
          <Table hover responsive>
            <thead>
              <tr>
                { config.columns.map((column) => <th key={ column.key }>{ column.label }</th>) }
                <th>{ 'Action' }</th>
              </tr>
            </thead>
            <tbody>
              {
                !isLoading && listPagination.rows.map((record) => (
                  <tr key={ record.id }>
                    {
                      config.columns.map((column) => (
                        <td key={ column.key }>{ formatCell(column, record[column.key]) }</td>
                      ))
                    }
                    <td className='text-nowrap'>
                      { !record.deleted_at && <Button color='info' size='sm' className='mr-2' onClick={ () => openEdit(record) }>
                        { 'Edit' }
                      </Button> }
                      {
                        config.destroy && (
                          <Button color={ record.deleted_at ? 'success' : 'warning' } size='sm' disabled={ isArchiving || (!record.deleted_at && resourceKey === 'badges' && record.is_editable === false) } title={ resourceKey === 'badges' && record.is_editable === false ? 'Badge sistem dilindungi' : undefined } onClick={ () => record.deleted_at ? handleRestore(record) : handleDelete(record) }>
                            { record.deleted_at ? 'Restore' : 'Archive' }
                          </Button>
                        )
                      }
                    </td>
                  </tr>
                ))
              }
              {
                !isLoading && filteredRecords.length === 0 && (
                  <tr><td colSpan={ config.columns.length + 1 } className='text-center text-muted'>{ 'No data' }</td></tr>
                )
              }
              {
                isLoading && (
                  <tr><td colSpan={ config.columns.length + 1 } className='text-center'>{ 'Loading...' }</td></tr>
                )
              }
            </tbody>
          </Table>
          <UrlPagination pagination={ listPagination } loading={ isLoading } />
        </CardBody>
      </Card>

      <Modal isOpen={ isModalOpen } toggle={ closeModal } size={ config.showProducts ? 'lg' : 'md' }>
        <form onSubmit={ handleSubmit }>
          <ModalHeader toggle={ closeModal }>
            { `${selectedRecord ? 'Edit' : 'Create'} ${config.singular}` }
          </ModalHeader>
          <ModalBody>
            { error && <div role='alert' className='alert alert-danger'>{ error }</div> }
            { config.fields.map(renderField) }

            {
              resourceKey === 'designers' && selectedRecord?.price_tag_logo_url && (
                <div className='mb-3'>
                  <Label className='d-block'>{ 'Current Price Tag Logo' }</Label>
                  <img src={ selectedRecord.price_tag_logo_url } alt='Price tag logo' style={{ maxHeight: '80px', maxWidth: '100%' }} />
                </div>
              )
            }

            {
              config.showProducts && selectedRecord && (
                <div className='mt-4'>
                  <h5>{ 'Produk yang Ditambahkan' }</h5>
                  {
                    selectedRecord.products?.length
                      ? (
                        <ul className='mb-0'>
                          {
                            selectedRecord.products.map((product) => (
                              <li key={ product.id }>{ `${product.sku || '-'} ${Number(product.selling_price || 0).toLocaleString()}` }</li>
                            ))
                          }
                        </ul>
                      )
                      : <div className='text-muted'>{ 'Tidak ada produk.' }</div>
                  }
                </div>
              )
            }
          </ModalBody>
          <ModalFooter>
            <Button type='button' color='secondary' onClick={ closeModal } disabled={ isSaving }>{ 'Cancel' }</Button>
            <Button type='submit' color='primary' disabled={ isSaving }>{ isSaving ? 'Saving...' : 'Save' }</Button>
          </ModalFooter>
        </form>
      </Modal>
    </>
  )
}

LegacySettingsResourcePage.propTypes    = propTypes
LegacySettingsResourcePage.defaultProps = defaultProps

export default LegacySettingsResourcePage
